import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import { Button, Card, Input, Select, Spin, Tag } from '@/shared/ui/primitives';
import EmptyState from '@/shared/ui/EmptyState';
import EditableCell from '../components/EditableCell';
import TranslationCreateDialog from '../components/TranslationCreateDialog';
import CoveragePanel from '../components/CoveragePanel';
import ImportExportPanel from '../components/ImportExportPanel';
import {
  useCreateTranslation,
  useDeleteTranslation,
  useSupportedLocales,
  useTranslationsList,
  useUpdateTranslation,
} from '../hooks/useTranslations';
import type { SupportedLocale, TranslationEntry } from '../api/translations.api';

const PAGE_SIZE = 50;

/**
 * Default columns shown in the list. ar+en are always shown so the
 * page is readable on a laptop; others are togglable from the locale
 * filter.
 */
const DEFAULT_LOCALE_COLUMNS = ['ar', 'en'];

export default function TranslationsListPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [namespace, setNamespace] = useState<string | undefined>(undefined);
  const [missingOnly, setMissingOnly] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [importExportOpen, setImportExportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TranslationEntry | null>(null);
  const [visibleLocales, setVisibleLocales] = useState<string[]>(DEFAULT_LOCALE_COLUMNS);
  const [showTechnical, setShowTechnical] = useState(false);

  const params = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      ...(search ? { search } : {}),
      ...(namespace ? { namespace } : {}),
    }),
    [page, search, namespace],
  );

  const listQuery = useTranslationsList(params);
  const localesQuery = useSupportedLocales();
  const createMut = useCreateTranslation();
  const updateMut = useUpdateTranslation();
  const deleteMut = useDeleteTranslation();

  const data = listQuery.data;
  const supportedLocales: SupportedLocale[] = localesQuery.data ?? [];
  const totalItems = data?.totalItems ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const missingOnPage = useMemo(() => {
    const rows = data?.content ?? [];
    return rows.reduce((sum, row) => {
      return sum + supportedLocales.filter((locale) => !row.translations[locale.code]?.trim()).length;
    }, 0);
  }, [data, supportedLocales]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (!missingOnly) return data.content;
    return data.content.filter((row) => {
      const v = row.translations[missingOnly];
      return v == null || v.trim() === '';
    });
  }, [data, missingOnly]);

  const submitSearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
  };
  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setNamespace(undefined);
    setMissingOnly(undefined);
    setPage(0);
  };

  return (
    <div>
      <PageHeader
        title={t('translations.title')}
        subtitle={t('translations.subtitle')}
        actionLabel={t('translations.actions.newText')}
        onAction={() => setCreateOpen(true)}
      />

      <div className="ds-row" style={{ gap: 12, marginBottom: 16 }}>
        <Card styles={{ body: { padding: 14 } }} style={{ flex: '1 1 220px' }}>
          <div className="ds-muted">{t('translations.health.totalTexts')}</div>
          <strong style={{ fontSize: 26 }}>{totalItems.toLocaleString()}</strong>
        </Card>
        <Card styles={{ body: { padding: 14 } }} style={{ flex: '1 1 220px' }}>
          <div className="ds-muted">{t('translations.health.missingOnPage')}</div>
          <strong style={{ fontSize: 26, color: missingOnPage > 0 ? 'var(--ds-danger)' : 'var(--ds-success)' }}>{missingOnPage.toLocaleString()}</strong>
        </Card>
        <Card styles={{ body: { padding: 14 } }} style={{ flex: '1 1 220px' }}>
          <div className="ds-muted">{t('translations.health.modules')}</div>
          <strong style={{ fontSize: 26 }}>{(data?.namespaces?.length ?? 0).toLocaleString()}</strong>
        </Card>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Button onClick={() => setCoverageOpen((v) => !v)}>
          {coverageOpen ? t('translations.actions.hideHealth') : t('translations.actions.showHealth')}
        </Button>
        <Button onClick={() => setImportExportOpen(true)}>{t('translations.actions.importExport')}</Button>
        <Button type={showTechnical ? 'primary' : 'default'} onClick={() => setShowTechnical((v) => !v)}>
          {showTechnical ? t('translations.actions.hideTechnical') : t('translations.actions.showTechnical')}
        </Button>
      </div>

      <CoveragePanel open={coverageOpen} />

      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <Input
          value={searchInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
          placeholder={t('translations.filters.searchPlaceholder')}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') submitSearch();
          }}
          style={{ minWidth: 280 }}
        />
        <Button onClick={submitSearch}>{t('common.search')}</Button>

        <Select
          value={namespace ?? null}
          onChange={(v) => {
            setNamespace(v ? String(v) : undefined);
            setPage(0);
          }}
          allowClear
          placeholder={t('translations.filters.module')}
          options={(data?.namespaces ?? []).map((n) => ({ value: n, label: moduleLabel(n, t) }))}
          style={{ width: 200 }}
        />

        <Select
          value={missingOnly ?? null}
          onChange={(v) => setMissingOnly(v ? String(v) : undefined)}
          allowClear
          placeholder={t('translations.filters.missingLanguage')}
          options={supportedLocales.map((l) => ({ value: l.code, label: t('translations.filters.missingIn', { language: localeLabel(l, t) }) }))}
          style={{ width: 220 }}
        />

        <Select
          value={visibleLocales}
          onChange={(v) =>
            setVisibleLocales(
              Array.isArray(v) ? (v as string[]) : v ? [String(v)] : DEFAULT_LOCALE_COLUMNS,
            )
          }
          mode="multiple"
          placeholder={t('translations.filters.visibleLanguages')}
          options={supportedLocales.map((l) => ({ value: l.code, label: localeLabel(l, t) }))}
          style={{ minWidth: 240 }}
        />

        <Button
          type={missingOnly === 'ar' ? 'primary' : 'default'}
          onClick={() => {
            setMissingOnly((current) => (current === 'ar' ? undefined : 'ar'));
            setPage(0);
          }}
        >
          {t('translations.actions.missingArabic')}
        </Button>
        <Button
          type={missingOnly === 'en' ? 'primary' : 'default'}
          onClick={() => {
            setMissingOnly((current) => (current === 'en' ? undefined : 'en'));
            setPage(0);
          }}
        >
          {t('translations.actions.missingEnglish')}
        </Button>
        <Button onClick={() => setVisibleLocales(['ar'])}>{t('translations.actions.focusArabic')}</Button>
        <Button onClick={() => setVisibleLocales(['en'])}>{t('translations.actions.focusEnglish')}</Button>
        <Button onClick={() => setVisibleLocales(DEFAULT_LOCALE_COLUMNS)}>
          {t('translations.actions.showBothLanguages')}
        </Button>

        {(search || namespace || missingOnly) && <Button onClick={clearFilters}>{t('common.clearFilters')}</Button>}

        <div style={{ marginLeft: 'auto', color: '#666' }}>
          {totalItems > 0 ? t('translations.results.total', { count: totalItems }) : null}
          {missingOnly && filteredRows.length !== data?.content.length && (
            <span style={{ marginLeft: 6, color: '#d4380d' }}>
              · {t('translations.results.missingFiltered', { count: filteredRows.length, language: missingOnly.toUpperCase() })}
            </span>
          )}
        </div>
      </div>

      {listQuery.isError ? (
        <div style={{ color: '#d4380d', padding: 16, border: '1px solid #ffccc7', borderRadius: 6 }}>
          {t('translations.errors.loadFailed')}{' '}
          <Button size="small" onClick={() => listQuery.refetch()}>
            {t('translations.actions.retry')}
          </Button>
        </div>
      ) : listQuery.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !data || filteredRows.length === 0 ? (
        <EmptyState title={t('translations.empty.title')} description={t('translations.empty.description')} />
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ background: '#fafafa' }}>
              <tr>
                <Th>{t('translations.columns.text')}</Th>
                <Th>{t('translations.columns.module')}</Th>
                {supportedLocales
                  .filter((l) => visibleLocales.includes(l.code))
                  .map((l) => (
                    <Th key={l.code} style={{ minWidth: 200 }}>
                      {localeLabel(l, t)}
                    </Th>
                  ))}
                <Th>{t('translations.columns.health')}</Th>
                <Th>{t('translations.columns.updated')}</Th>
                {showTechnical ? <Th>{t('translations.columns.technical')}</Th> : null}
                <Th style={{ width: 90, textAlign: 'right' }}>{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((entry) => (
                <Row
                  key={entry.key}
                  entry={entry}
                  locales={supportedLocales.filter((l) => visibleLocales.includes(l.code))}
                  allLocales={supportedLocales}
                  showTechnical={showTechnical}
                  t={t}
                  onEditLocale={(locale, value) =>
                    updateMut.mutateAsync({
                      key: entry.key,
                      input: { translations: { [locale]: value } },
                    })
                  }
                  onDelete={() => setDeleteTarget(entry)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalItems > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'flex-end',
            marginTop: 12,
          }}
        >
          <Button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            {t('translations.pagination.previous')}
          </Button>
          <span style={{ color: '#666' }}>
            {t('translations.pagination.page', { current: page + 1, total: totalPages })}
          </span>
          <Button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t('translations.pagination.next')}
          </Button>
        </div>
      )}

      <TranslationCreateDialog
        open={createOpen}
        namespaces={data?.namespaces ?? []}
        locales={supportedLocales}
        loading={createMut.isPending}
        onSubmit={async ({ key, namespace, translations }) => {
          await createMut.mutateAsync({ key, namespace, translations });
          setCreateOpen(false);
        }}
        onCancel={() => setCreateOpen(false)}
      />

      <ImportExportPanel
        open={importExportOpen}
        namespaces={data?.namespaces ?? []}
        onClose={() => setImportExportOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('translations.delete.title')}
        message={
          deleteTarget ? (
            <>
              {t('translations.delete.message', { text: displayText(deleteTarget, supportedLocales, t) })}
            </>
          ) : null
        }
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMut.mutate(deleteTarget.key, { onSettled: () => setDeleteTarget(null) });
        }}
      />
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 12px',
        borderBottom: '1px solid #eee',
        fontWeight: 600,
        fontSize: 13,
        color: '#444',
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Row({
  entry,
  locales,
  allLocales,
  showTechnical,
  t,
  onEditLocale,
  onDelete,
}: {
  entry: TranslationEntry;
  locales: SupportedLocale[];
  allLocales: SupportedLocale[];
  showTechnical: boolean;
  t: (key: string, options?: any) => string;
  onEditLocale: (locale: string, next: string) => Promise<unknown>;
  onDelete: () => void;
}) {
  const missing = allLocales.filter((l) => !entry.translations[l.code]?.trim());
  return (
    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td style={tdStyle}>
        <div style={{ fontWeight: 650 }}>{displayText(entry, allLocales, t)}</div>
        <div className="ds-muted" style={{ fontSize: 12, marginTop: 2 }}>
          {t('translations.row.tapToEdit')}
        </div>
      </td>
      <td style={tdStyle}>
        <span
          style={{
            fontSize: 12,
            background: '#f0f5ff',
            color: '#1d39c4',
            padding: '2px 8px',
            borderRadius: 12,
          }}
        >
          {moduleLabel(entry.namespace, t)}
        </span>
      </td>
      {locales.map((l) => {
        const value = entry.translations[l.code] ?? '';
        const missing = !value;
        return (
          <td
            key={l.code}
            style={{
              ...tdStyle,
              background: missing ? 'rgba(250, 173, 20, 0.08)' : undefined,
            }}
          >
            {missing && (
              <span
                style={{
                  display: 'inline-block',
                  marginBottom: 4,
                  fontSize: 11,
                  color: '#d4380d',
                  background: '#fff1f0',
                  padding: '1px 6px',
                  borderRadius: 8,
                }}
              >
                {t('translations.status.missing')}
              </span>
            )}
            <EditableCell
              value={value}
              onSave={(v) => onEditLocale(l.code, v)}
              rtl={l.rtl}
            />
          </td>
        );
      })}
      <td style={tdStyle}>
        {missing.length === 0 ? (
          <Tag color="success">{t('translations.status.complete')}</Tag>
        ) : (
          <Tag color="warning">{t('translations.status.missingCount', { count: missing.length })}</Tag>
        )}
      </td>
      <td style={tdStyle}>
        <div
          style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}
          title={
            entry.updatedBy && Object.keys(entry.updatedBy).length > 0
              ? t('translations.row.editedBy', { user: firstUpdater(entry.updatedBy) })
              : t('translations.row.updatedAt', { date: entry.updatedAt })
          }
        >
          {formatDate(entry.updatedAt, t)}
          {entry.updatedBy && Object.keys(entry.updatedBy).length > 0 && (
            <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
              {t('translations.row.byUser', { user: firstUpdater(entry.updatedBy) })}
            </div>
          )}
        </div>
      </td>
      {showTechnical ? (
        <td style={tdStyle}>
          <code style={{ fontSize: 12 }}>{entry.key}</code>
        </td>
      ) : null}
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <Button size="small" danger onClick={onDelete}>
          {t('common.delete')}
        </Button>
      </td>
    </tr>
  );
}

function displayText(entry: TranslationEntry, locales: SupportedLocale[], t: (key: string, options?: any) => string): string {
  const preferred = ['ar', 'en'];
  for (const locale of preferred) {
    const value = entry.translations[locale]?.trim();
    if (value) return value;
  }
  return t('translations.row.missingPrimary');
}

function localeLabel(locale: SupportedLocale, t: (key: string, options?: any) => string): string {
  if (locale.code === 'ar') return t('translations.languages.ar');
  if (locale.code === 'en') return t('translations.languages.en');
  return locale.nativeName || locale.name || locale.code.toUpperCase();
}

function moduleLabel(namespace: string, t: (key: string, options?: any) => string): string {
  const normalized = String(namespace).trim().toLowerCase();
  const known = knownModuleLabel(normalized, t);
  if (known) return known;
  const translated = t(`translations.modules.${normalized}`, { defaultValue: '' });
  if (translated) return translated;
  return namespace
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function knownModuleLabel(namespace: string, t: (key: string, options?: any) => string): string | null {
  const ar = t('translations.languages.ar') === 'العربية';
  const labels: Record<string, [string, string]> = {
    legacy: ['Legacy text', 'نصوص قديمة'],
    advertiser: ['Advertiser', 'المعلن'],
  };
  const label = labels[namespace];
  return label ? (ar ? label[1] : label[0]) : null;
}

function firstUpdater(updaters: Record<string, string>): string {
  const values = Object.values(updaters).filter(Boolean);
  if (values.length === 0) return '';
  // Strip the "system:" prefix the seeder uses so list rows aren't noisy.
  const cleaned = values.map((v) => (v.startsWith('system:') ? v.split(':')[1] : v));
  const unique = [...new Set(cleaned)];
  return unique.length === 1 ? unique[0] : `${unique[0]} +${unique.length - 1}`;
}

function formatDate(iso: string, t: (key: string, options?: any) => string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return t('common.notFound');
  const now = Date.now();
  const diff = Math.round((now - d.getTime()) / 1000);
  if (diff < 60) return t('translations.time.justNow');
  if (diff < 3600) return t('translations.time.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('translations.time.hoursAgo', { count: Math.floor(diff / 3600) });
  if (diff < 86400 * 7) return t('translations.time.daysAgo', { count: Math.floor(diff / 86400) });
  return d.toISOString().slice(0, 10);
}

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  verticalAlign: 'top',
};
